import type { JSX } from 'react';
import { Button } from '@/components/ui/button';
import { useCandidateExperiences } from '../hooks/useCandidateExperiences';
import { useExperienceDialog } from '../hooks/useExperienceDialog';
import { CandidateExperienceFormDialog } from './CandidateExperienceFormDialog';
import { CandidateExperienceItem } from './CandidateExperienceItem';
import { WizardFooterActions } from './WizardFooterActions';
import type { ContactsByExperience } from '../utils/contact-results';
import type { Experience } from '@/features/experiences/types/experience.types';
import type { Verification } from '@/features/verifications/types/verification.types';

interface CandidateExperienceStepProps {
  experiences: Experience[];
  contactsByExperience: ContactsByExperience;
  loadingExperienceIds?: string[];
  canContinue: boolean;
  onContinue: () => void;
}

function EmptyExperienceState({ onAdd }: { onAdd: () => void }): JSX.Element {
  return (
    <div className="space-y-3 text-center">
      <p className="font-medium">No work experience added yet</p>
      <p className="text-sm text-muted-foreground">Add a work experience before continuing.</p>
      <Button onClick={onAdd}>Add work experience</Button>
    </div>
  );
}

export function CandidateExperienceStep({
  experiences,
  contactsByExperience,
  loadingExperienceIds = [],
  canContinue,
  onContinue,
}: CandidateExperienceStepProps): JSX.Element {
  const { deleteExperience } = useCandidateExperiences();
  const dialog = useExperienceDialog();
  const hasExperiences = experiences.length > 0;
  // An experience missing from the map means its contact query failed; pass null
  // so the card never renders a failed load as a confirmed empty contact list.
  const contactsFor = (experienceId: string): Verification[] | null =>
    contactsByExperience[experienceId] ?? null;

  return (
    <div className="space-y-6">
      {hasExperiences ? (
        <div className="space-y-4">
          {experiences.map((experience) => (
            <CandidateExperienceItem
              key={experience.id}
              experience={experience}
              contacts={contactsFor(experience.id)}
              isContactsLoading={loadingExperienceIds.includes(experience.id)}
              onEdit={dialog.openEdit}
              onDelete={deleteExperience}
            />
          ))}
        </div>
      ) : (
        <EmptyExperienceState onAdd={dialog.openCreate} />
      )}
      <WizardFooterActions
        canContinue={canContinue}
        onAdd={dialog.openCreate}
        onContinue={onContinue}
      />
      <CandidateExperienceFormDialog
        open={dialog.open}
        onOpenChange={dialog.setOpen}
        mode={dialog.mode}
        experience={dialog.experience}
      />
    </div>
  );
}
